import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import AllocationSignalRContext from "features/allocations/contexts/SignalRContext";
import Link from "shared/components/navigation/links/AnchorLink";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import { allocationsGridModes } from "shared/constants/allocationsEnums";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { useAllocationsMessages } from "features/allocations/hooks/useAllocationsMesages";

const Context = React.createContext();

export const AllocationsProvider = ({ mode, ...props }) => {
    const [event, setEvent] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [gridParams, setGridParams] = useState({});
    const [notificationDisplayed, setNotificationDisplayed] = useState(false);
    const [userQueryAccess, setUserQueryAccess] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState({loading: false, page: 1, message: "", type: ""});
    const [columnsView, setColumnsView] = useState(4);
    const [groupBy, setGroupBy] = useState(null);
    const [sortBy, setSortBy] = useState(null);
    const [openFilters, setOpenFilters] = useState(() => {});
    const [totalAmountOfResults, setTotalAmountOfResults] = useState(0);
    const [query, setQuery] = useState(null);

    const messagesHubRef = useRef(null);
    const allocationHubConnectionIdRef = useRef(null);
    const session = useContext(SessionContext);
    const signalRsession = useContext(AllocationSignalRContext);
    const messagesRef = useRef([]);
    const employeesRef = useRef(employees);
    const { processMessagesQueue } = useAllocationsMessages();

    useEffect(() => {
        if (event) {
            messagesHubRef.current = signalRsession.messagesHub;
            allocationHubConnectionIdRef.current =
                signalRsession.allocationHubConnectionId;

            console.info(
                "SignalR - Allocations - Connection state",
                messagesHubRef.current.connection.connectionState
            );
        }
        if (event && messagesHubRef.current?.connection.connectionState !== "Connected") {
            console.error(
                "SignalR - Allocations - Can't connect to SignalR hub"
            );
        } else if (event) {
            console.info("SignalR - Allocations - Subscribe to events...");
            subscribeToEvent(messagesHubRef.current, event, session);
        }
        // signalRsession values are read into refs (stable mutation, no re-sub needed).
        // subscribeToEvent is not memoized — adding it would re-subscribe on every render.
        // session is intentionally excluded; re-subscription is driven by event name changes only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event]);

    const subscribeToEventAddMessage = (message) => {
        console.info("SignalR - Allocations - New message", message);
        addMessage(JSON.parse(message));
    };

    const subscribeToEvent = (hub, srEvent, srSession) => {
        console.info("SignalR - Allocations - Subscribe to events", srEvent);
        hub.off(srEvent);
        srSession &&
            srEvent &&
            hub.on(srEvent, (message) => {
                console.info(
                    "SignalR - Allocations - Start subscription",
                    srEvent
                );
                srSession && message && subscribeToEventAddMessage(message);
            });
    };

    const addMessage = (message) => {
        let validMessage = false;

        console.info("SignalR - Allocations - Process new message");

        const employee = employeesRef.current
            .flat()
            .find(
                (employee) =>
                    employee.id === message.UserId &&
                    employee.titleId === message.TitleId &&
                    employee.locationId === message.OfficeId &&
                    employee.summary?.some(
                        (allocation) => allocation.date === message.Date
                    )
            );

        validMessage =
            employee &&
            (message.UpdatedBy !== session.userID ||
                allocationHubConnectionIdRef.current !== message.ClientId);

        if (validMessage) {
            console.info("SignalR - Allocations - Show update alert");
            messagesRef.current.push(message);
            !notificationDisplayed && setNotificationDisplayed(true);
        } else {
            console.info("SignalR - Allocations - Ignore message");
        }
    };

    useEffect(() => {
        setEvent(getNotificationsEventName(mode));
    }, [mode]);

    useEffect(() => {
        employeesRef.current = employees;
    }, [employees]);

    useEffect(() => {
        messagesRef.current.length > 0 &&
            !notificationDisplayed &&
            setNotificationDisplayed(true);
        // messagesRef.current is not a valid dep — mutations don't trigger re-renders.
        // addMessage already sets notificationDisplayed directly when messages arrive.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationDisplayed]);

    useEffect(() => {
        notificationDisplayed && showNotification();
        // showNotification is not memoized — adding it would cause this effect to run
        // on every render as its function reference changes each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationDisplayed]);

    const getNotificationsEventName = (mode) => {
        console.info("SignalR - Allocations - Message mode", mode);
        switch (mode) {
            case allocationsGridModes.daily:
                return "newAllocationHour_Daily";
            case allocationsGridModes.weekly:
                return "newAllocationHour_Weekly";
            case allocationsGridModes.monthly:
                return "newAllocationHour_Monthly";
            default:
                throw new Error(`Invalid grid mode ${mode}`);
        }
    };

    const handleUpdate = () => {
        const stringCopy = JSON.stringify([...employeesRef.current]);
        const employeesCopy = JSON.parse(stringCopy);
        const updatedEmployees = processMessagesQueue(
            employeesCopy,
            messagesRef.current
        );
        setEmployees(updatedEmployees);
        messagesRef.current = [];
    };

    const handleOnRemoval = () => {
        setNotificationDisplayed(false);
    };

    const showNotification = () => {
        sendNotification(updateEmployeesMessage(), {
            duration: 0,
            id: "SignalR_Allocations",
            onRemoval: handleOnRemoval,
        });
    };

    const updateEmployeesMessage = () => {
        return (
            <>
                {`Allocations in this view have been updated.`}
                <Link
                    variant="secondary"
                    color={globalThis.config.colors.white}
                    href={void 0}
                    onClick={handleUpdate}
                >
                    Update
                </Link>
            </>
        );
    };

    const contextValue = useMemo(() => {
        return {
            employees,
            setEmployees,
            gridParams,
            setGridParams,
            isLoadingMore,
            setIsLoadingMore,
            columnsView,
            setColumnsView,
            groupBy,
            setGroupBy,
            sortBy,
            setSortBy,
            openFilters,
            setOpenFilters,
            userQueryAccess,
            setUserQueryAccess,
            totalAmountOfResults,
            setTotalAmountOfResults,
            query, 
            setQuery
        };
    }, [
        employees,
        gridParams,
        isLoadingMore,
        columnsView,
        groupBy,
        sortBy,
        openFilters,
        userQueryAccess,
        totalAmountOfResults,
        query
    ]);

    return mode === null ? (
        <div>Loading...</div>
    ) : (
        <Context.Provider value={contextValue}>
            {props.children}
        </Context.Provider>
    );
};

export default Context;

AllocationsProvider.propTypes = {
    mode: PropTypes.string,
    children: PropTypes.node
};

AllocationsProvider.defaultProps = {
    mode: allocationsGridModes.weekly,
};
